import { CircularProgress, ListItem, TextField, Typography } from '@mui/material'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { SpreadsheetInput } from '@omnea-app/fe-tables/data-grid/SpreadsheetDataGrid/SpreadsheetInput'
import { BaseAutocomplete } from '@omnea-app/omui/BaseAutocomplete'
import { UserItem } from '@omnea-app/packages/universal/users-and-groups/userItem'
import { getLabelForUser } from '@omnea-app/packages/universal/users/getLabelForUser'
import { UserFilter } from '@omnea-app/users/usersFrontendClient'
import { User } from '../types'
import { useSearchUsers } from '../useSearchUsers'
import { isOmneanInProdCustomerAccount } from './isOmneanInProdCustomerAccount'

type TArg<ReturnFullUser extends boolean | undefined> = ReturnFullUser extends true ? User : UserItem

export interface Props<ReturnFullUser extends boolean | undefined> {
  title?: string
  label?: string | React.ReactNode
  required?: boolean
  value: UserItem | User | null
  disabled?: boolean
  autoFocus?: boolean
  onChange: (newValue: TArg<ReturnFullUser> | null) => void
  onBlur?: () => void
  error?: boolean
  minHeight?: number
  helperText?: string
  returnFullUser?: ReturnFullUser
  preventDefaultOnClick?: boolean
  fullWidth?: boolean
  isAiPrefilled?: boolean
  usage?: 'field' | 'spreadsheet-cell'
  ariaInvalid?: boolean
  ariaDescribedBy?: string
  isClearable?: boolean
  userFilter?: UserFilter
}

export const getUsersName = (option: UserItem | User) =>
  'firstName' in option ? `${option.firstName} ${option.lastName}` : option.name

export const UserAutocomplete = <ReturnFullUser extends boolean | undefined = false>({
  value,
  onChange,
  onBlur,
  error,
  disabled,
  helperText,
  minHeight = 40,
  title,
  label,
  required,
  returnFullUser = false,
  autoFocus = false,
  preventDefaultOnClick = false,
  fullWidth = false,
  isAiPrefilled = false,
  usage = 'field',
  ariaInvalid,
  ariaDescribedBy,
  isClearable = true,
  userFilter = { onlyAdmins: false },
}: Props<ReturnFullUser>) => {
  const isSpreadsheetCell = usage === 'spreadsheet-cell'
  const {
    org,
    user: { isOmnean: loggedUserIsOmnean },
  } = useAuthenticatedUser()
  const { t } = useTranslation('shared')

  const formatResults = useCallback(
    (users?: User[]): User[] => {
      if (!users) return []

      // If the user logged in is an Omnean, it's okay to show Omneans as options
      // We like to assign ourselves to things when debugging/testing for customers
      if (loggedUserIsOmnean) return users

      // If the user logged in is NOT an omnean, then they shouldn't see
      // Omnea support people in the results
      return users.filter((user) => !isOmneanInProdCustomerAccount({ user, orgName: org?.name }))
    },
    [loggedUserIsOmnean, org?.name]
  )

  const [searchValue, setSearchValue] = useState<string>('')

  const { data: searchResults, isLoading } = useSearchUsers({
    search: searchValue,
    limit: 100,
    filter: userFilter,
    select: formatResults,
    minSearchLength: 1,
  })

  const preventDefaultOnClickProps =
    // Note(Diego): This is in here to fix SR-734, where clicking the autocomplete within a hyperlink would trigger the hyperlink
    preventDefaultOnClick === true
      ? {
          onClick: (e: any) => {
            e.preventDefault()
          },
        }
      : {}

  return (
    <BaseAutocomplete<UserItem | User, false, boolean, false>
      {...preventDefaultOnClickProps}
      data-testid="user-selector"
      value={value}
      inputValue={searchValue}
      onInputChange={(_, value) => setSearchValue(value)}
      options={searchResults ?? []}
      disabled={disabled}
      loading={isLoading}
      fullWidth={fullWidth}
      disableClearable={!isClearable}
      renderInput={(params) => {
        if (isSpreadsheetCell) {
          return (
            <SpreadsheetInput
              {...params.InputProps}
              {...preventDefaultOnClickProps}
              inputProps={{
                ...params.inputProps,
                'aria-invalid': ariaInvalid,
                'aria-describedby': ariaDescribedBy,
                'data-testid': `user-autocomplete-${title != null ? title : 'id'}`,
                'data-is-ai-prefilled': isAiPrefilled,
              }}
              autoFocus={autoFocus}
              placeholder={t('autocomplete.placeholder')}
              onBlur={onBlur}
              endAdornment={isLoading ? <CircularProgress color="secondary" size={20} /> : params.InputProps.endAdornment}
            />
          )
        }

        return (
          <TextField
            {...params}
            {...preventDefaultOnClickProps}
            label={label}
            required={required}
            autoFocus={autoFocus}
            placeholder={t('autocomplete.placeholder')}
            error={error}
            data-testid={`user-autocomplete-${title != null ? title : 'id'}`}
            helperText={helperText}
            onBlur={onBlur}
            data-is-ai-prefilled={isAiPrefilled}
            slotProps={{
              input: {
                sx: { minHeight, maxHeight: 200 },
                ...params.InputProps,
                endAdornment: isLoading ? <CircularProgress color="secondary" size={20} /> : params.InputProps.endAdornment,
              },
            }}
          />
        )
      }}
      multiple={false}
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      getOptionLabel={(option) => (typeof option === 'string' ? option : getLabelForUser(option))}
      onChange={(_, value) => {
        if (!value) {
          onChange(null)
          return
        }

        const formattedValue = returnFullUser
          ? value
          : {
              id: value.id,
              name: getUsersName(value),
              email: value.email,
            }

        onChange(formattedValue as TArg<ReturnFullUser>)
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderOption={(props, option) => {
        const userOptionLabel = getLabelForUser(option)
        return (
          <ListItem {...props} key={option.id}>
            <Typography sx={{ lineHeight: '28px' }}>{userOptionLabel}</Typography>
          </ListItem>
        )
      }}
    />
  )
}
